import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkle, ArrowClockwise, DownloadSimple, UploadSimple, CheckCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Area } from "../components/forms/fields";
import { CALL_TYPE_LABELS } from "../components/PersonCallLogCard";

const ACTION_OPTIONS = [
  ["client_note", "File on client record(s)"],
  ["employee_note", "File under employee(s)"],
  ["callout", "Record a call-out (attendance + follow-up)"],
  ["incident", "Draft incident report"],
  ["complaint", "Log a complaint"],
  ["schedule_change", "Flag schedule change on client"],
  ["hospitalization", "Flag hospitalization / facility update"],
  ["follow_up", "Create follow-up task"],
  ["general", "Log only (no routing)"],
];

const STATUS_CHIP = {
  new: ["New — not filed", "bg-stone-100 text-stone-500 border-stone-300"],
  pending_review: ["Awaiting office review", "bg-[#B7791F]/10 text-[#8a5d14] border-[#B7791F]/40"],
  filed: ["Filed", "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/40"],
};

export default function CallLogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const [perm, setPerm] = useState(null);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const [form, setForm] = useState({ doc_type: "phone_call", entry_date: new Date().toISOString().slice(0, 10), contact_name: "", contact_type: "", notes: "" });
  const [picked, setPicked] = useState([]); // [{kind,id,name}]
  const [current, setCurrent] = useState(null); // entry being reviewed/filed
  const [ai, setAi] = useState(null);
  const [actions, setActions] = useState({});
  const [answers, setAnswers] = useState("");
  const [fu, setFu] = useState({ needed: false, type: "call", deadline: "", assignee: "" });
  const [editNotes, setEditNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [guideOn, setGuideOn] = useState(false);
  const [turns, setTurns] = useState([]);
  const [guideInput, setGuideInput] = useState("");
  const [guideBusy, setGuideBusy] = useState(false);

  const [settings, setSettings] = useState(null);
  const [cleanJobs, setCleanJobs] = useState([]);
  const importRef = useRef(null);
  const cleanRef = useRef(null);
  const reviewRef = useRef(null);

  const loadEntries = async () => {
    try { const { data } = await api.get("/call-log"); setEntries(data.entries || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadSettings = async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/call-log/settings"); setSettings(data); } catch {}
    try { const { data } = await api.get("/call-log/clean-jobs"); setCleanJobs(data.jobs || []); } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/call-log/permission");
        setPerm(data);
        if (!data.allowed) return;
        loadEntries();
        api.get("/clients").then((r) => {
          const list = Array.isArray(r.data) ? r.data : r.data.clients || [];
          setClients(list);
        }).catch(() => {});
        if (data.is_admin) {
          api.get("/users").then((r) => {
            const list = Array.isArray(r.data) ? r.data : r.data.users || [];
            setStaff(list.filter((u) => u.role === "staff").sort((a, b) => ((a.active === false) - (b.active === false)) || (a.name || "").localeCompare(b.name || "")));
          }).catch(() => {});
          loadSettings();
        }
        const preClient = searchParams.get("client");
        const preEmp = searchParams.get("employee");
        if (preClient || preEmp) {
          setPicked([{ kind: preClient ? "client" : "employee", id: preClient || preEmp, name: "" }].filter(Boolean));
        }
      } catch { setPerm({ allowed: false }); }
    })();
  }, [isAdmin]); // eslint-disable-line

  // resolve names for pre-attached people once lists load
  useEffect(() => {
    setPicked((prev) => prev.map((p) => {
      if (p.name) return p;
      if (p.kind === "client") {
        const c = clients.find((x) => x.id === p.id);
        const nm = c?.forms?.intake?.full_name || c?.full_name;
        return nm ? { ...p, name: nm } : p;
      }
      const s = staff.find((x) => (x.id || x._id) === p.id);
      return s ? { ...p, name: s.name || s.email } : p;
    }));
  }, [clients, staff]);

  const clientName = (c) => c.forms?.intake?.full_name || c.full_name || c.client_code;

  const startReview = async (entry, runAi = true) => {
    setCurrent(entry);
    setEditNotes(entry.notes || "");
    setAi(entry.ai || null);
    setAnswers("");
    const base = {};
    (entry.ai ? [entry.ai.primary, ...(entry.ai.also || [])] : []).forEach((k) => { if (k) base[k] = true; });
    if (!entry.ai) base.client_note = entry.people?.some((p) => p.kind === "client");
    setActions(base);
    const f = entry.ai?.follow_up || entry.follow_up || {};
    setFu({ needed: !!f.needed, type: f.type || "call", deadline: f.deadline || "", assignee: f.assignee || "" });
    setPicked(entry.people?.length ? entry.people : picked);
    setTimeout(() => reviewRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    if (runAi && !entry.ai) await analyze(entry.id);
  };

  const analyze = async (entryId, ans = "") => {
    const id = entryId || current?.id;
    if (!id) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/call-log/${id}/classify`, { answers: ans });
      setAi(data.ai);
      const base = {};
      [data.ai.primary, ...(data.ai.also || [])].forEach((k) => { if (k) base[k] = true; });
      if (data.ai.follow_up?.needed) base.follow_up = true;
      setActions(base);
      if (data.ai.follow_up?.needed) setFu({ needed: true, type: data.ai.follow_up.type || "call", deadline: data.ai.follow_up.deadline || "", assignee: data.ai.follow_up.assignee || "" });
      if (data.ai.suggested_people?.length) {
        setPicked((prev) => {
          const have = new Set(prev.map((p) => `${p.kind}:${p.id}`));
          return [...prev, ...data.ai.suggested_people.filter((p) => !have.has(`${p.kind}:${p.id}`))];
        });
      }
      if (data.ai.summary) setEditNotes((n) => n); // keep user text; summary shown separately
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAnalyzing(false); }
  };

  const guideSend = async (text) => {
    const t = [...turns, ...(text ? [{ role: "user", text }] : [])];
    if (text) setTurns(t);
    setGuideBusy(true);
    try {
      const { data } = await api.post("/call-log/guide", { turns: t, doc_type: form.doc_type });
      if (data.done) {
        const f = data.fields;
        setForm({ doc_type: f.doc_type, entry_date: f.entry_date, contact_name: f.contact_name, contact_type: f.contact_type, notes: f.notes });
        const np = [];
        (f.clients || []).forEach((n) => { const c = clients.find((x) => clientName(x) === n); if (c) np.push({ kind: "client", id: c.id, name: n }); });
        (f.employees || []).forEach((n) => { const s = staff.find((x) => (x.name || x.email) === n); if (s) np.push({ kind: "employee", id: s.id || s._id, name: n }); });
        setPicked(np);
        setTurns([]);
        setGuideInput("");
        setGuideOn(false);
        toast.success("Filled in from your conversation — check it, then save");
      } else {
        setTurns([...t, { role: "assistant", text: data.question }]);
        setGuideInput("");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGuideBusy(false); }
  };

  const submitNew = async () => {
    if (!form.notes.trim()) { toast.error("Write or dictate the note first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/call-log", { ...form, people: picked.filter((p) => p.id) });
      toast.success(isAdmin ? "Entry saved — let the AI read it" : "Sent to the office for review");
      if (isAdmin) { await startReview(data.entry); }
      else { setCurrent(null); }
      setForm({ doc_type: "phone_call", entry_date: new Date().toISOString().slice(0, 10), contact_name: "", contact_type: "", notes: "" });
      setPicked([]);
      loadEntries();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const fileEntry = async () => {
    if (!current) return;
    const chosen = Object.keys(actions).filter((k) => actions[k]);
    if (!chosen.length) { toast.error("Pick at least one action (or “Log only”)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/call-log/${current.id}/file`, {
        people: picked.filter((p) => p.id),
        actions, follow_up: actions.follow_up ? { ...fu, needed: true } : { needed: false },
        incident_category: "Other", complaint_category: "Other",
        cleaned_notes: editNotes,
      });
      toast.success(data.filed_to?.length ? `Filed → ${data.filed_to.join(", ")}` : "Filed to the log");
      setCurrent(null); setAi(null);
      loadEntries();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markFollowUpDone = async (id) => {
    try { await api.post(`/call-log/${id}/follow-up-done`); toast.success("Follow-up marked done"); loadEntries(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const discard = async (id) => {
    if (!window.confirm("Discard this entry?")) return;
    try { await api.delete(`/call-log/${id}`); toast.success("Discarded"); if (current?.id === id) setCurrent(null); loadEntries(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/call-log/import", fd, { timeout: 300000 });
      toast.success(`Imported ${data.imported} entries (${data.duplicates} duplicates skipped, ${data.masked_people} people masked)`);
      loadEntries();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (importRef.current) importRef.current.value = ""; }
  };

  const doClean = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/call-log/clean-upload", fd);
      toast.success("Cleanup started — this takes a few minutes for large files");
      const t = setInterval(async () => {
        try {
          const { data } = await api.get("/call-log/clean-jobs");
          setCleanJobs(data.jobs || []);
          if ((data.jobs || []).every((j) => j.status !== "processing")) clearInterval(t);
        } catch { clearInterval(t); }
      }, 5000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { if (cleanRef.current) cleanRef.current.value = ""; }
  };

  const downloadClean = async (j) => {
    try {
      const res = await api.get(`/call-log/clean-jobs/${j.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `cleaned-${j.filename || "calls.xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => entries.filter((e) => {
    if (filter === "follow_up") return e.follow_up?.needed && !e.follow_up?.done;
    if (filter === "pending_review") return e.status === "pending_review";
    return true;
  }), [entries, filter]);

  if (perm === null) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  if (!perm.allowed) {
    return (
      <div className="max-w-3xl" data-testid="call-log-disabled">
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Call &amp; Documentation Log</h1>
        <p className="mt-3 rounded-lg border border-stone-200 bg-white p-5 text-sm text-stone-500">
          Call logging isn't enabled for your account. Ask the office to turn it on for you.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="call-log-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Call &amp; Documentation Log</h1>
      <p className="mt-1 text-sm text-stone-500">
        One form for every call, text, visit, or observation. Write or dictate the note — the AI reads it, asks if anything is unclear,
        and suggests where it belongs. You confirm; nothing files itself.
      </p>

      {/* ---- new entry ---- */}
      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="call-entry-form">
        <div className="flex items-center justify-between gap-2">
          <p className="font-heading text-sm font-semibold text-stone-900">New entry</p>
          <button type="button" onClick={() => { setGuideOn(!guideOn); if (!guideOn && turns.length === 0) guideSend(""); }}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${guideOn ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:border-primary"}`}
            data-testid="call-guide-toggle">
            <Sparkle size={12} /> {guideOn ? "Close guide" : "Guide me — AI asks the questions"}
          </button>
        </div>

        {guideOn && (
          <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="call-guide-panel">
            <div className="max-h-56 space-y-2 overflow-y-auto">
              {turns.map((t, i) => (
                <p key={i} className={t.role === "assistant" ? "text-sm font-medium text-primary" : "ml-8 text-sm text-stone-600"} data-testid={`guide-turn-${i}`}>
                  {t.role === "assistant" ? t.text : `You: ${t.text}`}
                </p>
              ))}
              {guideBusy && <p className="text-xs text-stone-400" data-testid="guide-thinking">Thinking…</p>}
            </div>
            <div className="mt-3">
              <Area label="Your answer (speak or type)" tid="call-guide-input" rows={2}
                value={guideInput} onChange={(e) => setGuideInput(e.target.value)} />
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={() => guideSend(guideInput.trim())} disabled={guideBusy || !guideInput.trim()}
                className="rounded-full bg-primary text-primary-foreground" data-testid="call-guide-send">
                {guideBusy ? "…" : "Send"}
              </Button>
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="call-type-picker">
          {Object.entries(CALL_TYPE_LABELS).filter(([k]) => ["phone_call", "text", "email", "in_person", "home_visit", "introduction", "note"].includes(k)).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, doc_type: k })}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.doc_type === k ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400"}`}
              data-testid={`call-type-${k}`}>{l}</button>
          ))}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Date of the call / interaction *</Label>
            <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="border-stone-300" data-testid="call-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Who was the contact?</Label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="border-stone-300" placeholder="e.g. Barbara from St. Mary's, or the client's daughter" data-testid="call-contact-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Contact type</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="contact-type-picker">
              {[["", "—"], ["client", "Client"], ["employee", "Employee"], ["family", "Family"], ["facility", "Facility / doctor"], ["other", "Other"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, contact_type: k })}
                  className={`rounded-full border px-2.5 py-1 text-xs ${form.contact_type === k ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500"}`}
                  data-testid={`contact-type-${k || "none"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Regarding client</Label>
            <select value={picked.find((p) => p.kind === "client")?.id || ""}
              onChange={(e) => {
                const c = clients.find((x) => x.id === e.target.value);
                setPicked([...picked.filter((p) => p.kind !== "client"), ...(c ? [{ kind: "client", id: c.id, name: clientName(c) }] : [])]);
              }}
              className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="call-client-select">
              <option value="">— not about a client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
            </select>
          </div>
          {isAdmin && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Regarding employee</Label>
              <select value={picked.find((p) => p.kind === "employee")?.id || ""}
                onChange={(e) => {
                  const s = staff.find((x) => (x.id || x._id) === e.target.value);
                  setPicked([...picked.filter((p) => p.kind !== "employee"), ...(s ? [{ kind: "employee", id: s.id || s._id, name: s.name || s.email }] : [])]);
                }}
                className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="call-employee-select">
                <option value="">— not about an employee —</option>
                {staff.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name || s.email}{s.active === false ? " (former)" : ""}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Area label="What happened? (speak it or type it — the AI will tidy and route it)" tid="call-notes-input"
            rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Barbara from St. Mary's called — Patti is going to rehab, steady antibiotics, they don't know which facility yet…" />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submitNew} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="call-submit-button">
            {busy ? "Saving…" : isAdmin ? "Save & analyze with AI" : "Send to office for review"}
          </Button>
        </div>
      </div>

      {/* ---- AI review card ---- */}
      {current && isAdmin && (
        <div ref={reviewRef} className="mt-6 rounded-xl border-2 border-primary/40 bg-white p-6 shadow-sm" data-testid="call-review-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading text-sm font-semibold text-stone-900">Review &amp; file — {CALL_TYPE_LABELS[current.doc_type]} · {current.entry_date}</p>
            <Button size="sm" variant="ghost" onClick={() => { setCurrent(null); setAi(null); }} className="text-stone-400" data-testid="review-close">Close</Button>
          </div>

          <div className="mt-3">
            <Area label="Note (tidy or edit before filing — the original is always preserved)" tid="review-notes"
              rows={4} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
          </div>

          {analyzing && <p className="mt-3 text-xs text-stone-400" data-testid="ai-analyzing">AI is reading the note…</p>}
          {ai && !analyzing && (
            <div className="mt-4 space-y-4" data-testid="ai-panel">
              <div className="rounded-lg bg-primary/5 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">AI summary</p>
                <p className="mt-1 text-sm text-stone-700" data-testid="ai-summary">{ai.summary}</p>
                {ai.urgency && ai.urgency !== "routine" && (
                  <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${ai.urgency === "urgent" ? "bg-[#C0392B]/10 text-[#C0392B]" : "bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid="ai-urgency">
                    {ai.urgency.toUpperCase()}
                  </span>
                )}
              </div>
              {(ai.questions || []).length > 0 && (
                <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="ai-questions">
                  <p className="text-xs font-semibold text-[#8a5d14]">The AI is asking:</p>
                  <ul className="mt-1 list-inside list-disc text-xs text-stone-600">
                    {ai.questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                  <div className="mt-2 flex gap-2">
                    <Input value={answers} onChange={(e) => setAnswers(e.target.value)} placeholder="Answer here, then re-analyze" className="border-stone-300 bg-white text-sm" data-testid="ai-answers-input" />
                    <Button size="sm" variant="outline" onClick={() => analyze(null, answers)} disabled={analyzing || !answers.trim()} className="shrink-0 border-stone-300" data-testid="ai-reanalyze">
                      <ArrowClockwise size={13} className="mr-1" /> Re-analyze
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-stone-700">Who does this apply to?</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="review-people">
                  {picked.length === 0 && <span className="text-xs text-stone-400">No one attached yet — use the client/employee pickers in the form, or let the AI suggest.</span>}
                  {picked.map((p) => (
                    <span key={`${p.kind}:${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground" data-testid={`review-person-${p.id}`}>
                      {p.kind === "client" ? "Client" : "Staff"}: {p.name || p.id}
                      <button onClick={() => setPicked(picked.filter((x) => x !== p))} className="text-stone-400 hover:text-[#C0392B]" data-testid={`review-person-remove-${p.id}`}>×</button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-700">File it as…</p>
                <div className="mt-1.5 grid gap-1.5" data-testid="review-actions">
                  {ACTION_OPTIONS.map(([k, l]) => (
                    <label key={k} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${actions[k] ? "border-primary/50 bg-primary/5 text-stone-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`action-${k}`}>
                      <input type="checkbox" checked={!!actions[k]}
                        onChange={(e) => {
                          const next = { ...actions, [k]: e.target.checked };
                          if (e.target.checked && k !== "general") next.general = false;
                          if (e.target.checked && k === "general") Object.keys(next).forEach((x) => { if (x !== "general") next[x] = false; });
                          setActions(next);
                          if (k === "follow_up" && e.target.checked) setFu((f) => ({ ...f, needed: true }));
                        }} />
                      {l}
                    </label>
                  ))}
                </div>
                {actions.follow_up && (
                  <div className="mt-2 grid gap-2 rounded-lg border border-stone-200 bg-stone-50 p-3 sm:grid-cols-3" data-testid="follow-up-editor">
                    <select value={fu.type} onChange={(e) => setFu({ ...fu, type: e.target.value })} className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="fu-type">
                      <option value="call">Call</option><option value="task">Task</option><option value="visit">Visit</option>
                    </select>
                    <Input type="date" value={fu.deadline} onChange={(e) => setFu({ ...fu, deadline: e.target.value })} className="border-stone-300 bg-white" data-testid="fu-deadline" />
                    <Input value={fu.assignee} onChange={(e) => setFu({ ...fu, assignee: e.target.value })} placeholder="Who follows up" className="border-stone-300 bg-white" data-testid="fu-assignee" />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => analyze(null)} disabled={analyzing} className="border-stone-300" data-testid="review-reanalyze-plain">
                  <Sparkle size={14} className="mr-1" /> Re-analyze
                </Button>
                <Button onClick={fileEntry} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="review-file-button">
                  {busy ? "Filing…" : "Confirm & file"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- the log ---- */}
      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="call-log-list-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-heading text-sm font-semibold text-stone-900">The log</p>
          <div className="flex gap-1.5" data-testid="log-filters">
            {[["", "All"], ["follow_up", "Follow-ups open"], ["pending_review", "Needs review"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === k ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-500"}`}
                data-testid={`filter-${k || "all"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-3 space-y-1.5" data-testid="call-log-list">
          {filtered.length === 0 && <p className="text-xs text-stone-400" data-testid="call-log-empty">No entries yet.</p>}
          {filtered.map((e) => {
            const chip = STATUS_CHIP[e.status] || STATUS_CHIP.new;
            const people = (e.people || []).map((p) => p.name).filter(Boolean);
            return (
              <div key={e.id} className="rounded-lg border border-stone-100 px-4 py-2.5" data-testid={`log-entry-${e.id}`}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} className="min-w-0 flex-1 text-left" data-testid={`log-expand-${e.id}`}>
                    <p className="truncate text-sm text-stone-800">
                      <span className="font-medium">{e.entry_date}</span>
                      <span className="text-stone-400"> · {CALL_TYPE_LABELS[e.doc_type] || e.doc_type}</span>
                      {e.contact_name && <span className="text-stone-600"> · {e.contact_name}</span>}
                      {e.regarding_name && <span className="text-stone-400"> re: {e.regarding_name}</span>}
                    </p>
                    <p className="mt-0.5 line-clamp-1 text-xs text-stone-500">{e.notes}</p>
                  </button>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${chip[1]}`} data-testid={`log-status-${e.id}`}>{chip[0]}</span>
                  {e.follow_up?.needed && !e.follow_up?.done && (
                    <span className="shrink-0 rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a5d14]" data-testid={`log-fup-${e.id}`}>
                      FOLLOW-UP{e.follow_up.deadline ? ` ${e.follow_up.deadline}` : ""}
                    </span>
                  )}
                </div>
                {expanded === e.id && (
                  <div className="mt-2 border-t border-stone-100 pt-2" data-testid={`log-detail-${e.id}`}>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-stone-600">{e.notes}</p>
                    {e.notes_original && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] text-stone-400" data-testid={`log-original-toggle-${e.id}`}>Show original (before cleanup)</summary>
                        <p className="mt-1 whitespace-pre-wrap rounded bg-stone-50 p-2 text-[11px] text-stone-500">{e.notes_original}</p>
                      </details>
                    )}
                    {people.length > 0 && <p className="mt-1 text-[11px] text-stone-400">Attached to: {people.join(", ")}</p>}
                    {e.filed_to?.length > 0 && <p className="mt-0.5 text-[11px] text-[#1e8449]">Filed to: {e.filed_to.join(", ")}</p>}
                    <p className="mt-0.5 text-[11px] text-stone-400">Logged by {e.created_by}{e.source === "import" ? " · historical import" : ""}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {isAdmin && e.status !== "filed" && (
                        <Button size="sm" onClick={() => startReview(e)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`log-review-${e.id}`}>
                          <Sparkle size={13} className="mr-1" /> Review &amp; file
                        </Button>
                      )}
                      {isAdmin && e.follow_up?.needed && !e.follow_up?.done && (
                        <Button size="sm" variant="outline" onClick={() => markFollowUpDone(e.id)} className="rounded-full border-stone-300" data-testid={`log-fup-done-${e.id}`}>
                          <CheckCircle size={13} className="mr-1" /> Follow-up done
                        </Button>
                      )}
                      {(isAdmin || (e.created_by_uid === user?.id && e.status === "new")) && (
                        <Button size="sm" variant="outline" onClick={() => discard(e.id)} className="rounded-full border-stone-300 text-stone-400 hover:text-[#C0392B]" data-testid={`log-delete-${e.id}`}>
                          <Trash size={13} />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- admin: settings, import, cleanup ---- */}
      {isAdmin && settings && (
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="call-log-settings-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Employee access</p>
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="employee-logging-toggle">
            <input type="checkbox" checked={settings.employee_logging}
              onChange={async (e) => {
                try { await api.put("/call-log/settings", { employee_logging: e.target.checked }); loadSettings(); toast.success(e.target.checked ? "Employee logging on" : "Employee logging off"); }
                catch (err) { toast.error(formatApiError(err)); }
              }} />
            Employees may log calls &amp; notes (their entries always come to the office for review first)
          </label>
          {settings.employee_logging && (
            <div className="mt-3 grid max-h-44 gap-1 overflow-y-auto rounded-lg border border-stone-200 p-2 sm:grid-cols-2" data-testid="call-log-staff-toggles">
              {settings.staff.map((s) => (
                <label key={s.uid} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-stone-600 hover:bg-stone-50" data-testid={`access-toggle-${s.uid}`}>
                  <input type="checkbox" checked={s.can_log_calls}
                    onChange={async (e) => {
                      try { await api.put(`/call-log/access/${s.uid}`, { allowed: e.target.checked }); loadSettings(); }
                      catch (err) { toast.error(formatApiError(err)); }
                    }} />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="call-log-import-card">
            <p className="font-heading text-sm font-semibold text-stone-900">Import history</p>
            <p className="mt-1 text-xs text-stone-500">Bring in the Interactions spreadsheet. Client &amp; family names are masked automatically in this environment.</p>
            <input ref={importRef} type="file" accept=".xlsx" className="hidden" data-testid="call-log-import-input"
              onChange={(e) => doImport(e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => importRef.current?.click()} disabled={busy} className="mt-3 rounded-full border-stone-300" data-testid="call-log-import-button">
              <UploadSimple size={14} className="mr-1" /> {busy ? "Importing…" : "Upload call log xlsx"}
            </Button>
          </div>
          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="call-log-clean-card">
            <p className="font-heading text-sm font-semibold text-stone-900">Typo cleanup</p>
            <p className="mt-1 text-xs text-stone-500">Upload the spreadsheet — get back a copy with an AI-cleaned notes column next to the untouched original.</p>
            <input ref={cleanRef} type="file" accept=".xlsx" className="hidden" data-testid="call-log-clean-input"
              onChange={(e) => doClean(e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => cleanRef.current?.click()} className="mt-3 rounded-full border-stone-300" data-testid="call-log-clean-button">
              <Sparkle size={14} className="mr-1" /> Clean a file
            </Button>
            {cleanJobs.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="clean-jobs-list">
                {cleanJobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-xs" data-testid={`clean-job-${j.id}`}>
                    <span className="text-stone-500">
                      {j.filename} — {j.status === "processing" ? `cleaning ${j.done}/${j.total}…` : j.status === "done" ? `done (${j.total} notes)` : `failed: ${j.error || "error"}`}
                    </span>
                    {j.status === "done" && (
                      <button onClick={() => downloadClean(j)} className="inline-flex items-center gap-1 font-medium text-primary hover:underline" data-testid={`clean-download-${j.id}`}>
                        <DownloadSimple size={12} /> Download
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
